import { useState } from 'react';
import type { Participant } from '@sfc/sdk';
import { CheckYourEmail } from './auth/CheckYourEmail';
import { ConsumeLink } from './auth/ConsumeLink';
import { SignInScreen } from './auth/SignInScreen';
import { useParticipant } from './auth/useParticipant';
import { forgetSignInToken, signInTokenFromUrl, usePathname } from './auth/route';
import { SignedIn } from './SignedIn';

/**
 * One question: is anybody signed in, and if not, where are they in getting
 * there?
 *
 * The link landing is checked before the session, because somebody arriving on
 * one may already have a live session from another device and should still be
 * allowed to spend the link they were sent rather than being silently dropped
 * onto the deck.
 */
export function App(): React.JSX.Element {
  const path = usePathname();
  const token = path.startsWith('/sign-in/') ? signInTokenFromUrl() : undefined;
  // The link owns the outcome while it is being spent, so the session check
  // does not run alongside it. See the note in useParticipant.
  const { state, signedIn, signOut } = useParticipant(!token);
  const [sentTo, setSentTo] = useState<string>();

  if (token) {
    return (
      <ConsumeLink
        token={token}
        onSignedIn={(participant: Participant) => {
          setSentTo(undefined);
          signedIn(participant);
        }}
        onFailed={() => {
          // Now, on the way out, rather than when the failure was discovered.
          forgetSignInToken();
          setSentTo(undefined);
        }}
      />
    );
  }

  if (state.status === 'checking') {
    return (
      <main className="screen">
        <p className="loading" data-testid="checking">
          One moment…
        </p>
      </main>
    );
  }

  if (state.status === 'unreachable') {
    return (
      <main className="screen">
        <div className="pane">
          <header className="pane__head">
            <span className="mark" aria-hidden="true">
              SFC
            </span>
            <h1>We cannot reach the server</h1>
          </header>
          <p className="notice notice--problem" role="status" data-testid="unreachable-notice">
            Start the backend, or point <code>VITE_API_BASE_URL</code> at a running one.
          </p>
        </div>
      </main>
    );
  }

  if (state.status === 'signed-in') {
    return <SignedIn participant={state.participant} onSignOut={signOut} />;
  }

  if (sentTo) return <CheckYourEmail email={sentTo} onStartOver={() => setSentTo(undefined)} />;

  return <SignInScreen onSent={setSentTo} onSignedIn={signedIn} />;
}
